import React, { useCallback, useEffect, useState } from 'react';
import { Loader2, Plus, Trash2, Check, AlertCircle } from 'lucide-react';
import { adminKbGetSources, adminKbSetSources, type KbRegisteredSource, type KbSourceRegKind } from '../../services/adminService';

const KINDS: KbSourceRegKind[] = ['news', 'research', 'tech', 'books', 'blogs', 'rss'];
const field = 'rounded-md border border-secondary-300 dark:border-secondary-700 bg-white dark:bg-secondary-900 px-2 py-1 text-sm focus:outline-none focus:border-secondary-500';

/** Curated reputable-source list for one learning domain — the researcher prefers these when learning. Editing
 *  here only STEERS research; the anti-poison + benchmark gates still protect the domain. */
const SourceRegistryEditor: React.FC<{ domain: string }> = ({ domain }) => {
  const [rows, setRows] = useState<KbRegisteredSource[] | null>(null);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const load = useCallback(async () => {
    setError('');
    try { setRows((await adminKbGetSources(domain)).sources); }
    catch (e: any) { setError(e?.message || 'Failed to load sources'); }
  }, [domain]);
  useEffect(() => { void load(); }, [load]);

  const patch = (i: number, p: Partial<KbRegisteredSource>) => setRows((r) => (r || []).map((s, j) => (j === i ? { ...s, ...p } : s)));
  const add = () => setRows((r) => [...(r || []), { url: '', kind: 'news' }]);
  const remove = (i: number) => setRows((r) => (r || []).filter((_, j) => j !== i));

  const save = async () => {
    setSaving(true); setError(''); setSaved(false);
    try {
      const clean = (rows || []).filter((s) => s.url.trim());
      setRows(await adminKbSetSources(domain, clean));
      setSaved(true); setTimeout(() => setSaved(false), 1500);
    } catch (e: any) { setError(e?.message || 'Failed to save'); } finally { setSaving(false); }
  };

  if (rows === null) return <div className="py-3 flex justify-center text-secondary-400"><Loader2 className="w-4 h-4 animate-spin" /></div>;

  return (
    <div className="mt-3 rounded-lg bg-secondary-50 dark:bg-secondary-800/50 p-3">
      <div className="mb-2 text-xs text-secondary-500">Reputable sources the researcher prefers for this domain.</div>
      {error && <div className="mb-2 flex items-center gap-1.5 text-xs text-rose-600"><AlertCircle className="w-3.5 h-3.5" />{error}</div>}
      <div className="space-y-1.5">
        {rows.map((s, i) => (
          <div key={i} className="flex items-center gap-1.5">
            <input value={s.url} onChange={(e) => patch(i, { url: e.target.value })} placeholder="https://reuters.com"
              className={`${field} flex-1 min-w-0`} />
            <select value={s.kind} onChange={(e) => patch(i, { kind: e.target.value as KbSourceRegKind })} className={field}>
              {KINDS.map((k) => <option key={k} value={k}>{k}</option>)}
            </select>
            <button onClick={() => remove(i)} className="p-1 text-secondary-400 hover:text-rose-600" aria-label="Remove source"><Trash2 className="w-4 h-4" /></button>
          </div>
        ))}
        {rows.length === 0 && <div className="text-xs text-secondary-400 py-1">No sources yet — learning falls back to the open web.</div>}
      </div>
      <div className="mt-2 flex items-center gap-2">
        <button onClick={add} className="inline-flex items-center gap-1 text-xs text-secondary-500 hover:text-secondary-900 dark:hover:text-white"><Plus className="w-3.5 h-3.5" /> Add</button>
        <button onClick={() => void save()} disabled={saving}
          className="ml-auto inline-flex items-center gap-1.5 rounded-md bg-secondary-900 text-white dark:bg-white dark:text-secondary-900 px-2.5 py-1 text-xs font-medium hover:opacity-90 disabled:opacity-40">
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : saved ? <Check className="w-3.5 h-3.5" /> : null}{saved ? 'Saved' : 'Save sources'}
        </button>
      </div>
    </div>
  );
};

export default SourceRegistryEditor;
